/**
 * AICreatorLiveModal — reusable full-screen modal that embeds an AI creator's
 * live avatar via the provider iframe (Trulience for V1).
 *
 * Reusable: driven entirely by the `creator` prop (see src/config/aiCreators.js),
 * so any creator on any iframe-based provider renders through this one component.
 *
 * Behaviour:
 *  - Loading skeleton (blurred thumbnail + spinner) until the iframe `load` fires.
 *  - 8s timeout OR iframe error → graceful error fallback with Retry + Close.
 *  - Emits ai_creator_chat_started (mount / connect-intent) and
 *    ai_creator_iframe_loaded (on iframe load).
 *
 * Pattern mirrors the existing ZoeGalleryModal shell (MUI Modal, dark overlay),
 * so it introduces no new UI primitives.
 */
import React, { useState, useEffect, useRef, useCallback } from "react";
import { Box, Typography, IconButton, CircularProgress, Button, Modal } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import { trackAICreator } from "../../utils/aiCreatorAnalytics";

const LOAD_TIMEOUT_MS = 8000;

// Build the Trulience Public-avatar embed URL from the creator config.
function buildTrulienceUrl(creator) {
  const base = `https://www.trulience.com/avatar/${creator.avatarId}`;
  const p = new URLSearchParams();
  if (creator.connect) p.set("connect", "1");
  if (creator.msgOnConnect) p.set("msgOnConnect", creator.msgOnConnect);
  if (creator.chatScreenBGColor) p.set("chatScreenBGColor", creator.chatScreenBGColor);
  const qs = p.toString();
  return qs ? `${base}?${qs}` : base;
}

export default function AICreatorLiveModal({ creator, surface = "homepage", onClose, onFallbackToChat }) {
  const [status, setStatus] = useState("loading"); // loading | ready | error
  const startRef = useRef(null);
  const timerRef = useRef(null);
  const [nonce, setNonce] = useState(0); // bump to force iframe remount on retry

  const src = buildTrulienceUrl(creator);

  // Fire chat_started once on mount (connect-intent), arm the load timeout.
  useEffect(() => {
    startRef.current = Date.now();
    trackAICreator("ai_creator_chat_started", {
      creator_id: creator.creator_id,
      provider: creator.provider,
      entry: "modal",
      surface,
    });
    timerRef.current = setTimeout(() => {
      setStatus((s) => (s === "loading" ? "error" : s));
    }, LOAD_TIMEOUT_MS);
    return () => clearTimeout(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nonce]);

  const handleLoad = useCallback(() => {
    clearTimeout(timerRef.current);
    setStatus("ready");
    trackAICreator("ai_creator_iframe_loaded", {
      creator_id: creator.creator_id,
      provider: creator.provider,
      load_ms: startRef.current ? Date.now() - startRef.current : null,
      avatar_mode: "public",
      surface,
    });
  }, [creator, surface]);

  const handleError = useCallback(() => {
    clearTimeout(timerRef.current);
    setStatus("error");
  }, []);

  const retry = () => {
    setStatus("loading");
    setNonce((n) => n + 1);
  };

  return (
    <Modal open onClose={onClose}>
      <Box
        sx={{
          position: "fixed", inset: 0, bgcolor: "rgba(0,0,0,0.92)",
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          zIndex: 9999, px: { xs: 1, sm: 2 },
        }}
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        {/* Close */}
        <IconButton onClick={onClose} aria-label="Close"
          sx={{ position: "absolute", top: 16, right: 16, color: "#fff", bgcolor: "rgba(255,255,255,0.1)", zIndex: 2 }}>
          <CloseIcon />
        </IconButton>

        <Typography sx={{ color: "#ccc", fontSize: "0.8rem", fontWeight: 600, mb: 1 }}>
          {creator.display_name} — Live
        </Typography>

        {/* Iframe stage */}
        <Box sx={{
          position: "relative",
          width: { xs: "100%", sm: 420 },
          height: { xs: "70vh", sm: 640 },
          maxWidth: "96vw", maxHeight: "82vh",
          borderRadius: 2, overflow: "hidden",
          border: "1px solid rgba(233,30,140,0.35)",
          boxShadow: "0 8px 48px rgba(0,0,0,0.8)",
          bgcolor: "#0d0d1a",
        }}>
          {status !== "error" && (
            <Box
              key={nonce}
              component="iframe"
              title={`${creator.display_name} live avatar`}
              src={src}
              onLoad={handleLoad}
              onError={handleError}
              allow="camera; microphone; fullscreen; autoplay; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              sx={{ width: "100%", height: "100%", border: 0, display: status === "ready" ? "block" : "none" }}
            />
          )}

          {/* Loading skeleton */}
          {status === "loading" && (
            <Box sx={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column",
              alignItems: "center", justifyContent: "center", gap: 2 }}>
              <Box component="img" src={creator.thumbnail} alt=""
                sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover",
                  filter: "blur(14px) brightness(0.4)" }} />
              <CircularProgress size={34} sx={{ color: "#e91e8c", zIndex: 1 }} />
              <Typography sx={{ color: "#fff", fontSize: "0.8rem", zIndex: 1 }}>
                Connecting to {creator.display_name}…
              </Typography>
            </Box>
          )}

          {/* Error fallback */}
          {status === "error" && (
            <Box sx={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column",
              alignItems: "center", justifyContent: "center", gap: 1.5, p: 3, textAlign: "center" }}>
              <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "0.95rem" }}>
                {creator.display_name} is having a moment 😅
              </Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.7)", fontSize: "0.8rem" }}>
                The live avatar couldn't connect. Try again, or chat with her instead.
              </Typography>
              <Box sx={{ display: "flex", gap: 1.5, mt: 1, flexWrap: "wrap", justifyContent: "center" }}>
                <Button onClick={retry} startIcon={<RefreshIcon />} variant="outlined"
                  sx={{ borderColor: "rgba(233,30,140,0.5)", color: "#e91e8c", textTransform: "none",
                    "&:hover": { borderColor: "#e91e8c", bgcolor: "rgba(233,30,140,0.08)" } }}>
                  Retry
                </Button>
                {onFallbackToChat && (
                  <Button onClick={() => { onClose(); onFallbackToChat(); }} variant="contained"
                    sx={{ bgcolor: "#e91e8c", "&:hover": { bgcolor: "#c2185b" }, textTransform: "none", fontWeight: 700 }}>
                    Chat with {creator.display_name}
                  </Button>
                )}
              </Box>
            </Box>
          )}
        </Box>

        <Typography sx={{ color: "#666", fontSize: "0.65rem", mt: 1.5, maxWidth: 420, textAlign: "center" }}>
          AI companion. 18+. Conversations are with an AI avatar.
        </Typography>
      </Box>
    </Modal>
  );
}
